import { addDays, startOfMonth, getDay } from 'date-fns'
import { EVENT_INSTANCE_TYPES } from 'settings/constants/service'
import { isNotInvalidDate, formatDateToBEFormatDateFns, formatDateToFEFormatDateFns } from 'utils/date'
import { get } from 'utils/lodash'

/**
 * Create a standard event object
 * @param {Date} date - Event date
 * @param {number} eventId - Event ID
 * @param {Object} serviceData - Service data containing action type and business line
 * @param {Array} existingEvents - Existing events to check for matches
 * @returns {Object} Event object
 */
export const createEvent = (date, eventId, serviceData = {}, existingEvents = []) => {
  if (!isNotInvalidDate(date)) {
    throw new Error('Invalid date provided to createEvent')
  }

  const dateString = formatDateToBEFormatDateFns(date)

  // Find matching existing event on the same date
  const existingEvent = existingEvents.find(event => event.serviceDate === dateString)

  const baseEvent = {
    id: `${eventId}`,
    serviceDateObj: new Date(date),
    dayOfMonth: date.getDate(),
    serviceDate: dateString,
    formattedServiceDate: formatDateToFEFormatDateFns(date),
    actionType: get(serviceData, 'actionType', ''),
    actionName: get(serviceData, 'actionName', `New ${get(serviceData, 'actionType', '')}`),
    businessLine: get(serviceData, 'businessLine', ''),
    businessLineType: get(serviceData, 'businessLineType', ''),
    configuredServiceEventType: get(serviceData, 'configuredServiceEventType', ''),
  }

  if (existingEvent) {
    return {
      ...baseEvent,
      ...existingEvent,
    }
  }

  return baseEvent
}

/**
 * Find the first occurrence of a specific day of week starting from a given date
 * @param {Date} startDate - Starting date to search from
 * @param {number} targetDayOfWeek - Target day of week (0-6)
 * @returns {Date|null} First occurrence of the day or null if not found within 7 days
 */
const findFirstDayOfWeek = (startDate, targetDayOfWeek) => {
  let cursor = new Date(startDate)
  let attempts = 0

  while (getDay(cursor) !== targetDayOfWeek && attempts < 7) {
    cursor = addDays(cursor, 1)
    attempts++
  }

  return getDay(cursor) === targetDayOfWeek ? cursor : null
}

/**
 * Calculate months to add based on interval type
 * @param {string} intervalType - 'month', 'quarter', 'year'
 * @param {number} recurrencePer - Number of intervals
 * @returns {number} Number of months to add
 */
const getMonthsToAdd = (intervalType, recurrencePer) => {
  switch (intervalType) {
    case 'month':
      return recurrencePer
    case 'quarter':
      return recurrencePer * 3
    case 'year':
      return recurrencePer * 12
    default:
      return recurrencePer
  }
}

/**
 * Generate weekly events for a specific day of week
 * @param {number} dayOfWeek - Day of week (0-6)
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @param {number} recurrencePer - Week intervals
 * @param {Object} serviceData - Service data
 * @param {Array} existingEvents - Existing events
 * @param {Object} eventIdRef - Reference to event ID counter
 * @returns {Array} Generated events
 */
const generateWeeklyEventsForDay = (dayOfWeek, startDate, endDate, recurrencePer, serviceData, existingEvents, eventIdRef) => {
  const events = []
  const firstOccurrence = findFirstDayOfWeek(startDate, dayOfWeek)

  if (!firstOccurrence) return events

  let cursor = firstOccurrence
  while (cursor <= endDate) {
    events.push(createEvent(cursor, eventIdRef.value++, serviceData, existingEvents))
    cursor = addDays(cursor, recurrencePer * 7)
  }

  return events
}

/**
 * Generate period-based events for a specific day of week
 * @param {Object} options - Event generation options
 * @param {number} options.dayOfWeek - Day of week (0-6)
 * @param {Date} options.startDate - Start date
 * @param {Date} options.endDate - End date
 * @param {string} options.intervalType - 'month', 'quarter', 'year'
 * @param {number} options.recurrencePer - Period intervals
 * @param {Object} options.serviceData - Service data
 * @param {Array} options.existingEvents - Existing events
 * @param {Object} options.eventIdRef - Reference to event ID counter
 * @returns {Array} Generated events
 */
const generatePeriodEventsForDay = ({
  dayOfWeek,
  startDate,
  endDate,
  intervalType,
  recurrencePer,
  serviceData,
  existingEvents,
  eventIdRef,
}) => {
  const events = []
  const monthsToAdd = getMonthsToAdd(intervalType, recurrencePer)
  let currentPeriod = new Date(startDate)

  while (currentPeriod <= endDate) {
    const firstOccurrence = findFirstDayOfWeek(startOfMonth(currentPeriod), dayOfWeek)

    if (firstOccurrence && firstOccurrence.getMonth() === currentPeriod.getMonth() && firstOccurrence <= endDate) {
      events.push(createEvent(firstOccurrence, eventIdRef.value++, serviceData, existingEvents))
    }

    currentPeriod.setMonth(currentPeriod.getMonth() + monthsToAdd)
  }

  return events
}

/**
 * Generate events based on recurrence type and parameters
 * @param {string} intervalType - 'day', 'week', 'month', 'quarter', 'year'
 * @param {number} recurrencePer - Number of intervals between events
 * @param {Date} startDate - Start date for generation
 * @param {Date} endDate - End date for generation
 * @param {number[]} activeDays - Array of selected day numbers (0-6)
 * @param {Object} serviceData - Service data for event metadata
 * @param {Array} existingEvents - Existing events to check for matches
 * @returns {Array} Array of event objects
 */
export const generateRecurrenceEvents = (
  intervalType,
  recurrencePer,
  startDate,
  endDate,
  activeDays,
  serviceData = {},
  existingEvents = []
) => {
  const events = []
  const eventIdRef = { value: 1 }

  if (intervalType === 'day') {
    let cursor = new Date(startDate)
    while (cursor <= endDate) {
      events.push(createEvent(cursor, eventIdRef.value++, serviceData, existingEvents))
      cursor = addDays(cursor, recurrencePer)
    }
    return events
  }

  activeDays.forEach(dayOfWeek => {
    const dayEvents =
      intervalType === 'week'
        ? generateWeeklyEventsForDay(dayOfWeek, startDate, endDate, recurrencePer, serviceData, existingEvents, eventIdRef)
        : generatePeriodEventsForDay({
            dayOfWeek,
            startDate,
            endDate,
            intervalType,
            recurrencePer,
            serviceData,
            existingEvents,
            eventIdRef,
          })

    events.push(...dayEvents)
  })

  return events
}

/**
 * Generate events for daily intervals with specific dates or day of week
 * @param {number} recurrencePer - Number of days between events
 * @param {Date} startDate - Start date for generation
 * @param {Date} endDate - End date for generation
 * @param {Object} params - Event parameters
 * @param {Object} serviceData - Service data for event metadata
 * @param {Array} existingEvents - Existing events to check for matches
 * @returns {Array} Array of event objects
 */
export const generateDailyEvents = (recurrencePer, startDate, endDate, params, serviceData = {}, existingEvents = []) => {
  const events = []
  let eventId = 1

  if (params.eventInstanceType === EVENT_INSTANCE_TYPES.SPECIFIC_DATE) {
    // Generate events every N days starting from specified day of month
    const dayOfMonths = Array.isArray(params.dayOfMonth) ? params.dayOfMonth : [params.dayOfMonth]

    dayOfMonths.forEach(dayOfMonth => {
      // Start from the first occurrence of this day of month on or after startDate
      let cursor = new Date(startDate.getFullYear(), startDate.getMonth(), dayOfMonth)
      if (cursor < startDate) {
        cursor.setMonth(cursor.getMonth() + 1)
      }

      while (cursor <= endDate) {
        events.push(createEvent(cursor, eventId++, serviceData, existingEvents))
        cursor = addDays(cursor, recurrencePer)
      }
    })
  } else if (params.eventInstanceType === EVENT_INSTANCE_TYPES.DAY_OF_WEEK) {
    // Generate events every N days for specified days of week
    const dayOfWeeks = Array.isArray(params.dayOfWeek) ? params.dayOfWeek : [params.dayOfWeek]

    dayOfWeeks.forEach(dayOfWeek => {
      // Skip invalid day of week values
      if (dayOfWeek < 0 || dayOfWeek > 6) {
        return
      }

      // Find first occurrence of this day of week on or after startDate
      let cursor = new Date(startDate)
      let attempts = 0
      while (cursor.getDay() !== dayOfWeek && attempts < 7) {
        cursor = addDays(cursor, 1)
        attempts++
      }

      // If we found the correct day of week, generate events
      if (cursor.getDay() === dayOfWeek) {
        while (cursor <= endDate) {
          events.push(createEvent(cursor, eventId++, serviceData, existingEvents))
          cursor = addDays(cursor, recurrencePer)
        }
      }
    })
  }

  return events
}

/**
 * Normalize event parameters to arrays for consistent processing
 * @param {Object} params - Event parameters
 * @returns {Object} Normalized parameters with arrays
 */
const normalizeEventParams = params => {
  const dayOfMonths = Array.isArray(params?.dayOfMonth) ? params?.dayOfMonth : [params?.dayOfMonth]
  const dayOfWeeks = Array.isArray(params.dayOfWeek) ? params.dayOfWeek : [params.dayOfWeek]
  const occurrences = Array.isArray(params.occurrence) ? params.occurrence : [params.occurrence]

  const numOccurrences =
    params.eventInstanceType === EVENT_INSTANCE_TYPES.SPECIFIC_DATE ? dayOfMonths.length : Math.min(dayOfWeeks.length, occurrences.length)

  return { dayOfMonths, dayOfWeeks, occurrences, numOccurrences }
}

/**
 * Generate target date based on event instance type
 * @param {Object} params - Event parameters
 * @param {Date} currentPeriod - Current period date
 * @param {string} intervalType - Interval type
 * @param {Object} normalizedParams - Normalized parameters
 * @param {number} index - Index for arrays
 * @returns {Date|null} Target date or null
 */
const generateTargetDate = (params, currentPeriod, intervalType, normalizedParams, index) => {
  if (params.eventInstanceType === EVENT_INSTANCE_TYPES.SPECIFIC_DATE) {
    return getSpecificDateInPeriod(currentPeriod, intervalType, normalizedParams.dayOfMonths[index])
  }

  if (params.eventInstanceType === EVENT_INSTANCE_TYPES.DAY_OF_WEEK) {
    const periodBounds = getPeriodBounds(currentPeriod, intervalType)
    return findNthDayOfWeekInPeriod(
      periodBounds.start,
      periodBounds.end,
      normalizedParams.dayOfWeeks[index],
      normalizedParams.occurrences[index]
    )
  }

  return null
}

/**
 * Process events for a single period
 * @param {Object} options - Event processing options
 * @param {Date} options.currentPeriod - Current period date
 * @param {Object} options.params - Event parameters
 * @param {string} options.intervalType - Interval type
 * @param {Date} options.startDate - Start date
 * @param {Date} options.endDate - End date
 * @param {Object} options.serviceData - Service data
 * @param {Array} options.existingEvents - Existing events
 * @param {Object} options.eventIdRef - Event ID reference
 * @param {Object} options.normalizedParams - Normalized parameters
 * @returns {Array} Events for this period
 */
const processEventsForPeriod = ({
  currentPeriod,
  params,
  intervalType,
  startDate,
  endDate,
  serviceData,
  existingEvents,
  eventIdRef,
  normalizedParams,
}) => {
  const events = []

  for (let i = 0; i < normalizedParams.numOccurrences; i++) {
    const targetDate = generateTargetDate(params, currentPeriod, intervalType, normalizedParams, i)

    if (targetDate && targetDate >= startDate && targetDate <= endDate) {
      events.push(createEvent(targetDate, eventIdRef.value++, serviceData, existingEvents))
    }
  }

  return events
}

/**
 * Generate events for period-based intervals (monthly, quarterly, yearly)
 * @param {string} intervalType - 'month', 'quarter', 'year'
 * @param {number} recurrencePer - Number of intervals between events
 * @param {Date} startDate - Start date for generation
 * @param {Date} endDate - End date for generation
 * @param {Object} params - Event parameters
 * @param {Object} serviceData - Service data for event metadata
 * @param {Array} existingEvents - Existing events to check for matches
 * @returns {Array} Array of event objects
 */
export const generatePeriodBasedEvents = (
  intervalType,
  recurrencePer,
  startDate,
  endDate,
  params,
  serviceData = {},
  existingEvents = []
) => {
  const events = []
  const eventIdRef = { value: 1 }
  let currentPeriod = new Date(startDate)
  const monthsToAdd = getMonthsToAdd(intervalType, recurrencePer)
  const normalizedParams = normalizeEventParams(params)

  while (currentPeriod <= endDate) {
    const periodEvents = processEventsForPeriod({
      currentPeriod,
      params,
      intervalType,
      startDate,
      endDate,
      serviceData,
      existingEvents,
      eventIdRef,
      normalizedParams,
    })

    events.push(...periodEvents)
    currentPeriod.setMonth(currentPeriod.getMonth() + monthsToAdd)
  }

  return events
}

/**
 * Generate non-weekly events with specific dates or day occurrences
 * @param {string} intervalType - 'day', 'month', 'quarter', 'year'
 * @param {number} recurrencePer - Number of intervals between events
 * @param {Date} startDate - Start date for generation
 * @param {Date} endDate - End date for generation
 * @param {Object} params - Event parameters
 * @param {string} params.eventInstanceType - 'DayOfWeek' or 'SpecificDate'
 * @param {number|number[]|string|string[]} params.dayOfMonth - Day of month (1-31, -1, or 'Last' for last day)
 * @param {number|number[]} params.dayOfWeek - Day of week number (0-6)
 * @param {number|number[]} params.occurrence - Which occurrence (1-4, -1 for last)
 * @param {Object} serviceData - Service data for event metadata
 * @param {Array} existingEvents - Existing events to check for matches
 * @returns {Array} Array of event objects sorted by date
 */
export const generateNonWeeklyEvents = (intervalType, recurrencePer, startDate, endDate, params, serviceData = {}, existingEvents = []) => {
  if (intervalType === 'day') {
    return generateDailyEvents(recurrencePer, startDate, endDate, params, serviceData, existingEvents).sort(
      (a, b) => new Date(a.serviceDate) - new Date(b.serviceDate)
    )
  }

  return generatePeriodBasedEvents(intervalType, recurrencePer, startDate, endDate, params, serviceData, existingEvents)
}

/**
 * Get specific date within a period (month/quarter/year)
 * @param {Date} periodDate - Date within the period
 * @param {string} intervalType - 'month', 'quarter', 'year'
 * @param {number|string} dayOfMonth - Day of month (1-31, -1, or 'Last' for last day)
 * @returns {Date|null} Target date or null if invalid
 */
export const getSpecificDateInPeriod = (periodDate, intervalType, dayOfMonth) => {
  if (!isNotInvalidDate(periodDate)) {
    return null
  }

  let resultDate

  if (dayOfMonth === -1 || dayOfMonth === 'Last') {
    // Last day of the period
    if (intervalType === 'month') {
      resultDate = new Date(periodDate.getFullYear(), periodDate.getMonth() + 1, 0)
    } else if (intervalType === 'quarter') {
      const quarterEndMonth = Math.floor(periodDate.getMonth() / 3) * 3 + 2
      resultDate = new Date(periodDate.getFullYear(), quarterEndMonth + 1, 0)
    } else {
      resultDate = new Date(periodDate.getFullYear(), 11, 31)
    }
  } else {
    // Specific day of the period
    const dayNum = parseInt(dayOfMonth, 10)
    if (intervalType === 'month') {
      resultDate = new Date(periodDate.getFullYear(), periodDate.getMonth(), dayNum)
    } else if (intervalType === 'quarter') {
      const quarterStartMonth = Math.floor(periodDate.getMonth() / 3) * 3
      resultDate = new Date(periodDate.getFullYear(), quarterStartMonth, dayNum)
    } else {
      resultDate = new Date(periodDate.getFullYear(), 0, dayNum)
    }
  }

  return isNotInvalidDate(resultDate) ? resultDate : null
}

/**
 * Get period bounds (start and end dates)
 * @param {Date} periodDate - Date within the period
 * @param {string} intervalType - 'month', 'quarter', 'year'
 * @returns {Object} Object with start and end dates
 */
export const getPeriodBounds = (periodDate, intervalType) => {
  if (!isNotInvalidDate(periodDate)) {
    return { start: null, end: null }
  }

  if (intervalType === 'month') {
    return {
      start: new Date(periodDate.getFullYear(), periodDate.getMonth(), 1),
      end: new Date(periodDate.getFullYear(), periodDate.getMonth() + 1, 0),
    }
  } else if (intervalType === 'quarter') {
    const quarterStartMonth = Math.floor(periodDate.getMonth() / 3) * 3
    return {
      start: new Date(periodDate.getFullYear(), quarterStartMonth, 1),
      end: new Date(periodDate.getFullYear(), quarterStartMonth + 3, 0),
    }
  } else {
    return {
      start: new Date(periodDate.getFullYear(), 0, 1),
      end: new Date(periodDate.getFullYear(), 11, 31),
    }
  }
}

/**
 * Find the nth occurrence of a day of week within a period
 * @param {Date} periodStart - Start of the period
 * @param {Date} periodEnd - End of the period
 * @param {number} dayOfWeek - Day of week (0-6, where 0 is Sunday)
 * @param {number} occurrence - Which occurrence (1-4, -1 for last)
 * @returns {Date|null} The target date or null if not found
 */
export const findNthDayOfWeekInPeriod = (periodStart, periodEnd, dayOfWeek, occurrence) => {
  const dates = []
  let current = new Date(periodStart)

  // Find all occurrences of the day of week in the period
  while (current <= periodEnd) {
    if (current.getDay() === dayOfWeek) {
      dates.push(new Date(current))
    }
    current.setDate(current.getDate() + 1)
  }

  if (dates.length === 0) return null

  if (occurrence === -1) {
    // Last occurrence
    return dates[dates.length - 1]
  } else {
    // Nth occurrence (1-based)
    const index = occurrence - 1
    return dates[index] || null
  }
}
